/* eslint-disable no-useless-catch */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable max-len */
import i18n from '@/i18n';
import { AuthPermissions, AuthState, AuthTokens, CommpixCredentials, MantecaCredentials } from '@/store/interfaces/auth';
import { RootState } from '@/store/interfaces/rootState';
import authType from '@/store/types/auth';
import { base64URLEncode, sha256 } from '@/util/auth';
import RefreshToken from '@/util/classes/refreshTokenTime';
import { http } from '@/util/HTTP';
import { getAuthPayload, httpCommpix, tokenKey } from '@/util/HTTPCommpix';
import axiosRetry from 'axios-retry';
import axios, { AxiosError, AxiosResponse } from 'axios';
import crypto from 'crypto';
import { TGlueItemList } from '@parra-payments/glue-components';
import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { iCompanyConfiguration } from '../interfaces/configurations';
import {
  historyTransactionPermission,
  dinamicPaymentPermission,
  salesReportPermission,
  scanToPayPermission,
  supportPermission,
  staffPermissions,
  commpixIntegrations,
  masspaymentIntegrations,
  mantecaIntegrations,
  pvsIntegrations,
  withdrawalsPermission,
  bolepixPermissions,
  payinsQrPermissions,
  balancePermissions,
  payoutsConfigurationPermissions,
  pendingPayoutsPermissions,
  dailyTransactionsStatementPermissions,
  transferencesPermissions
} from '@/router/menus/permissions';
import configurations from '../types/configurations';

const namespaced = true;

const initialState = (): AuthState => {
  return {
    menu: [
      {
        id: 'home',
        title: 'Home',
        leftIcon: 'home',
        to: '/',
      },
      {
        id: 'sales',
        title: 'Transacciones',
        leftIcon: 'shopping-cart',
        rightIcon: 'chevron-down',
        collapse: false,
        childrens: [
          {
            id: 'history-transactions',
            title: 'Historial de transacciones',
            to: '/transaction-history',
            permissions: historyTransactionPermission,
          },
          {
            id: 'sales-report',
            title: i18n.t('salesReports.transactionStatistics.title').toString(),
            to: '/sales-report',
            permissions: salesReportPermission,
          },
          {
            id: 'daily-transactions-statement',
            title: i18n.t('salesReports.dailyTransactionsStatement.title').toString(),
            to: '/daily-transactions-statement',
            permissions: dailyTransactionsStatementPermissions,
          }
        ],
      },
      {
        id: 'payments',
        title: 'Cobros y Pagos',
        leftIcon: 'report-money',
        rightIcon: 'chevron-down',
        collapse: false,
        childrens: [
          {
            id: 'dynamic-links',
            title: 'Links de pago',
            to: '/dinamic-payments',
            permissions: dinamicPaymentPermission,
          },
          {
            id: 'qr',
            title: 'Pagos con QR',
            to: '/scan-to-pay',
            permissions: scanToPayPermission,
          },
          {
            id: 'bolepix',
            title: 'BolePIX',
            to: '/bolepix',
            permissions: bolepixPermissions,
          },
          {
            id: 'qr-payins',
            title: 'Payins QR',
            to: '/qr-payins',
            permissions: payinsQrPermissions,
          },
          {
            id: 'transferences',
            title: 'Transferencias',
            to: '/transferences',
            permissions: transferencesPermissions,
          }
        ],
      },
      {
        id: 'withdrawals',
        title: 'Pix',
        leftIcon: 'message-dollar',
        rightIcon: 'chevron-down',
        collapse: false,
        childrens: [
          {
            id: 'withdrawals',
            title: 'Comisiones',
            to: '/withdrawals',
            permissions: withdrawalsPermission,
          },
          {
            id: 'balance',
            title: 'Estado de cuenta',
            to: '/balance',
            permissions: balancePermissions,
          },
          {
            id: 'pending-payouts',
            title: 'Payouts Pendientes',
            to: '/pending-payouts',
            permissions: pendingPayoutsPermissions,
          },
          {
            id: 'payouts-configuration',
            title: 'Configuración Payouts',
            to: '/payouts-configuration',
            permissions: payoutsConfigurationPermissions,
          }
        ],
      },
      {
        id: 'integrations',
        title: 'Integraciones',
        leftIcon: 'link-plus',
        rightIcon: 'chevron-down',
        collapse: false,
        childrens: [
          {
            id: 'commpix',
            title: 'Comm.PIX',
            to: '/integrations-commpix',
            permissions: commpixIntegrations,
          },
          {
            id: 'masspayment',
            title: 'MassPayments.PIX',
            to: '/integrations-masspayments',
            permissions: masspaymentIntegrations,
          },
          {
            id: 'manteca',
            title: 'Manteca',
            to: '/integrations-manteca',
            permissions: mantecaIntegrations,
          },
          {
            id: 'pvs',
            title: 'PVS',
            to: '/integrations-pvs',
            permissions: pvsIntegrations,
          }
        ],
      },
      {
        id: 'staff',
        title: 'Gestión de staff',
        leftIcon: 'users',
        to: '/staff',
        permissions: staffPermissions,
      },
      {
        id: 'support',
        title: 'Soporte',
        leftIcon: 'phone-call',
        to: '/support',
        permissions: supportPermission,
      }
    ],
    companyConfigurations: {
      features: {
        payments: false,
        'payments.dynamic_links': false,
        'payments.qr': false,
        'payments.static_links': false,
        'payments.nemipaytag': false,
        staff: false,
        support: false,
        transactions: false,
        'transactions.summary': false,
        'transactions.transactions': false,
      },
      translations: {
        commerce: {
          plural: '',
          singular: '',
        },
        company: {
          plural: '',
          singular: '',
        },
      },
    },
    userSession: {
      access_token: '',
      created_at: 0,
      expires_in: 0,
      refresh_token: '',
      scope: '',
      token_type: '',
    },
    token: '',
    status: 'not_logged',
    canSeeCommerces: true,
    company: undefined,
    currentUser: {
      id: '',
      email: '',
      confirmed: false,
      profile: {
        first_name: '',
        last_name: '',
      },
      companies: [],
      company_users: [],
    },
    permission_groups: [],
    company_users: [],
    // now stored as credentials object (API returns object)
    commpixCredentials: {} as CommpixCredentials,
    // now stored as masspayment credentials object (API returns object)
    masspaymentCredentials: {} as CommpixCredentials,
    // manteca user identifier (API returns an object with UserAnyId)
    mantecaCredentials: {} as MantecaCredentials,
    // pvs credentials object (API returns object)
    pvsCredentials: {} as CommpixCredentials,
    // access token for commpix operations (kept separate)
    nemiApiToken: '',
  };
};

const state: AuthState = initialState();
const getters: GetterTree<AuthState, RootState> = {
  [authType.getters.GET_MENU]: (state): TGlueItemList[] => {
    return state.menu;
  },
  [authType.getters.AUTH_STATUS]: (state): string => {
    return state.status;
  },
  [authType.getters.IS_AUTHENTICATED]: state => {
    return !!state.token;
  },
  [authType.getters.AUTH_PERMISSIONS]: state => {
    return state.canSeeCommerces;
  },
  [authType.getters.PERMISSIONS]: state => {
    return state.permission_groups;
  },
  [authType.getters.GET_AUTH_USER]: state => {
    return state.currentUser;
  },
  [authType.getters.GET_COMPANY]: state => {
    return state.company;
  },
  [authType.getters.GET_USER_SESSION]: state => {
    return state.userSession;
  },
  // returns the commpix credentials object
  [authType.getters.GET_COMMPIX_CREDENTIALS]: state => {
    return state.commpixCredentials || {};
  },
  // compatibility: return the credential object
  [authType.getters.GET_COMMPIX_PRIMARY]: state => {
    return state.commpixCredentials || {};
  },
  // returns the masspayment credentials object
  [authType.getters.GET_MASSPAYMENT_CREDENTIALS]: state => {
    return state.masspaymentCredentials || {};
  },
  // compatibility: return the masspayment credential object
  [authType.getters.GET_MASSPAYMENT_PRIMARY]: state => {
    return state.masspaymentCredentials || {};
  },
  // returns the manteca credentials object
  [authType.getters.GET_MANTECA_CREDENTIALS]: state => {
    return state.mantecaCredentials || {};
  },
  // returns the pvs credentials object
  [authType.getters.GET_PVS_CREDENTIALS]: state => {
    return state.pvsCredentials || {};
  },
  // compatibility: return the pvs credential object
  [authType.getters.GET_PVS_PRIMARY]: state => {
    return state.pvsCredentials || {};
  },
  [authType.getters.GET_NEMI_API_TOKEN]: state => {
    return state.nemiApiToken;
  },
};
const mutations: MutationTree<AuthState> = {
  reset: state => {
    const initialStateFields = initialState();
    Object.keys(initialStateFields).forEach(key => {
      state[key] = initialStateFields[key];
    });
  },
  [authType.mutations.SET_MENU]: (state) => {
    state.menu = initialState().menu;
    state.menu = state.menu.filter((iterator) => {
      if (iterator.childrens && iterator.childrens.length > 0) {
        iterator.childrens = iterator.childrens.filter((child) => {
          if (Array.isArray(child.permissions) && child.permissions.length > 0) {
            const hasPerm = child.permissions.some((permission) => state.permission_groups.includes(permission));
            return hasPerm;
          }
          return true;
        });
        return iterator.childrens.length > 0;
      } else if ('permissions' in iterator) {
        const hasPerm = (iterator.permissions as string[]).some((permission) => state.permission_groups.includes(permission));
        return hasPerm;
      }
      return true;
    });
  },
  [authType.mutations.AUTH_REQUEST]: state => {
    state.status = 'loading';
  },
  [authType.mutations.AUTH_PERMISSIONS]: (state, resp) => {
    state.canSeeCommerces = resp;
  },
  [authType.mutations.AUTH_SUCCESS]: (state, resp: AuthTokens) => {
    state.status = 'success';
    state.userSession = resp;
    state.token = resp.access_token;
    localStorage.setItem('_token', resp.access_token);
    http.defaults.headers.common.Authorization = `Bearer ${resp.access_token}`;
  },
  [authType.mutations.AUTH_ERROR]: state => {
    state.status = 'error';
  },
  [authType.mutations.AUTH_LOGOUT]: async state => {
    await httpCommpix.post('/login/invalidate');
    state.token = '';
    state.commpixCredentials = initialState().commpixCredentials;
    state.masspaymentCredentials = initialState().masspaymentCredentials;
    state.mantecaCredentials = initialState().mantecaCredentials;
  },
  [authType.mutations.SET_AUTH_USER]: (state, data) => {
    state.currentUser = data;
    if (data.companies.length > 0) {
      state.company = data.companies[0].id;
      state.permission_groups = data.company_users[0].permission_group.permissions
        .map((permission: AuthPermissions) => permission.slug);
      http.defaults.headers.common['X-COMPANY-ID'] = state.company;
    }
  },
  [authType.mutations.SET_COMPANY]: (state, company) => {
    state.company = company;

    const findCompany = state.currentUser.company_users
      .find(c => c.company_id === company);
    if (findCompany) {
      const featuresFlagAllowed = Object.entries(state.companyConfigurations.features)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_key, value]) => value)
        .map(([key]) => key);

      state.permission_groups = [
        ...findCompany.permission_group.permissions.map((p) => p.slug),
        ...featuresFlagAllowed
      ];
    }

    http.defaults.headers.common['X-COMPANY-ID'] = state.company;
  },
  [authType.mutations.SET_COMPANY_CONFIGURATION]: (state, configurations: { data: iCompanyConfiguration }) => {
    state.companyConfigurations = configurations.data;
    const featuresFlagAllowed = Object.entries(configurations.data.features)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_key, value]) => value)
      .map(([key]) => key);

    state.permission_groups = state.permission_groups.concat(featuresFlagAllowed);
  },
  [authType.mutations.SET_COMMPIX_CREDENTIALS]: (state, credentials) => {
    state.commpixCredentials = credentials || {};
  },
  [authType.mutations.SET_MASSPAYMENT_CREDENTIALS]: (state, credentials) => {
    state.masspaymentCredentials = credentials || {};
  },
  [authType.mutations.SET_MANTECA_CREDENTIALS]: (state, credentials) => {
    state.mantecaCredentials = credentials || {};
  },
  [authType.mutations.SET_PVS_CREDENTIALS]: (state, credentials) => {
    state.pvsCredentials = credentials || {};
  },
  [authType.mutations.SET_COMMPIX_AUTH_TOKEN]: (state, resp) => {
    state.nemiApiToken = resp.token;
    localStorage.setItem(tokenKey, resp.token);
  },
};
const actions: ActionTree<AuthState, RootState> = {
  [authType.actions.COMPANY_CONFIGURATION]: ({ commit, }) => {
    return new Promise((resolve, reject) => {
      http
        .get('companies/configurations')
        .then((res: AxiosResponse) => {
          if (Object.keys(res.data).length > 0) {
            commit(authType.mutations.SET_COMPANY_CONFIGURATION, res.data);

            commit(
              `configurationModule/${configurations.mutations.SET_I18N_LOCAL_MESSAGE}`,
              res.data,
              { root: true, }
            );
          }
          resolve(res);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  },
  [authType.actions.AUTHORIZE]: async ({ commit, dispatch, }, user) => {
    try {
      const token: AxiosResponse = await getToken();

      const getUserToken = {
        grant_type: 'password',
        email: user.email,
        password: user.password,
        scope: 'company',
      };

      const config = {
        headers: {
          Authorization: `Bearer ${token.data.access_token}`,
        },
      };
      const userToken: AxiosResponse = await http.post(
        'oauth/token',
        getUserToken,
        config
      );
      commit(authType.mutations.AUTH_SUCCESS, userToken.data);
      const currentUser: AxiosResponse = await http.get('companies/user');
      commit(authType.mutations.SET_AUTH_USER, currentUser.data.data);
      await dispatch(authType.actions.AUTH_COMMPIX, currentUser.data.data.email);
      await dispatch(authType.actions.EXPIRES_IN);
    } catch (err) {
      throw err;
    }
  },
  [authType.actions.RE_FETCH_USER_INFO]: async ({ commit, }) => {
    const currentUser: AxiosResponse = await http.get('companies/user');
    commit(authType.mutations.SET_AUTH_USER, currentUser.data.data);
  },
  [authType.actions.SEND_PASSWORD_INSTRUCTIONS]: async (_, user) => {
    try {
      const token: AxiosResponse = await getToken();

      const config = {
        headers: {
          Authorization: `Bearer ${token.data.access_token}`,
        },
      };

      await http.post(
        'accounts/passwords',
        { data: { email: user.email, }, },
        config
      );
    } catch (err) {
      throw err;
    }
  },
  [authType.actions.RESET_PASSWORD]: async (_, data) => {
    try {
      const token: AxiosResponse = await getToken();

      const config = {
        headers: {
          Authorization: `Bearer ${token.data.access_token}`,
        },
      };
      await http.put('accounts/passwords', { data: data, }, config);
    } catch (err) {
      throw err;
    }
  },
  [authType.actions.SEND_UNLOCK_INSTRUCTIONS]: async (_, user) => {
    try {
      const token: AxiosResponse = await getToken();

      const config = {
        headers: {
          Authorization: `Bearer ${token.data.access_token}`,
        },
      };

      await http.post(
        'accounts/unlocks',
        { data: { email: user.email, }, },
        config
      );
    } catch (err) {
      throw err;
    }
  },
  [authType.actions.UNLOCK_ACCOUNT]: async (_, code: string) => {
    try {
      const token: AxiosResponse = await getToken();

      const config = {
        headers: {
          Authorization: `Bearer ${token.data.access_token}`,
        },
      };
      await http.post(`accounts/unlocks/${code}`, {}, config);
    } catch (err) {
      throw err;
    }
  },

  [authType.actions.REFRESH_TOKEN]: ({ state, commit, dispatch, }) => {
    if (!state.token) {
      return Promise.reject(new Error('No token to refresh'));
    }
    const getUserToken = {
      refresh_token: state.userSession.refresh_token,
      grant_type: 'refresh_token',
    };
    const config = {
      headers: {
        Authorization: `Bearer ${state.userSession.access_token}`,
      },
    };
    return http
      .post('oauth/token', getUserToken, config)
      .then((res: AxiosResponse) => {
        commit(authType.mutations.AUTH_SUCCESS, res.data);
        dispatch(authType.actions.EXPIRES_IN);
      })
      .catch(() => {
        dispatch(authType.actions.AUTH_LOGOUT);
        return Promise.reject(new Error('Refresh failed'));
      });
  },
  [authType.actions.EXPIRES_IN]: ({ state, dispatch, }) => {
    const timeToExpiredToken = new RefreshToken(state.userSession);
    const time = timeToExpiredToken.timeToRefresh();
    // time must not be exceed 2147483647 milliseconds
    if (time > 0) {
      setTimeout(() => {
        dispatch(authType.actions.REFRESH_TOKEN);
      }, time);
    } else {
      dispatch(authType.actions.REFRESH_TOKEN);
    }
  },
  [authType.actions.SET_COMPANY]: async ({ commit, }, company) => {
    return new Promise(resolve => {
      commit(authType.mutations.SET_COMPANY, company);
      resolve(true);
    });
  },
  [authType.actions.AUTH_LOGOUT]: ({ commit, }) => {
    return new Promise(resolve => {
      Promise.resolve(commit(authType.mutations.AUTH_LOGOUT))
        .then(() => {
          // Exec localstorage clean after logout call
          localStorage.removeItem('company-translations');
          localStorage.removeItem('_token');
          localStorage.removeItem(tokenKey);
          localStorage.removeItem('nemipay');
          commit('reset');
          resolve(true);
        });
    });
  },
  [authType.actions.SETUP_PASSWORD]: async (_, data:
    { password: string, password_confirmation: string, code: string }
  ) => {
    const token: AxiosResponse = await getToken(true);
    const config = {
      headers: {
        Authorization: `Bearer ${token.data.access_token}`,
      },
    };
    return new Promise((resolve, reject) => {
      http
        .post(
          'accounts/passwords/setup',
          { data, },
          config
        )
        .then((res: AxiosResponse) => {
          resolve(res);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  },

  [authType.actions.POST_COMMPIX_CREDENTIALS]: async ({ dispatch, }, data) => {
    return new Promise((resolve, reject) => {
      httpCommpix
        .post(
          '/security/commpix/credentials',
          data
        )
        .then(async (res: AxiosResponse) => {
          // after creating/updating credentials, refresh the credentials list for the current scope
          try {
            const companyId = data.companyId || data.company_id;
            const commerceId = data.commerceId || data.commerce_id;
            const branchId = data.branchId || data.branch_id;
            const user = data.user;
            if (companyId && commerceId && branchId) {
              await dispatch(authType.actions.GET_COMMPIX_CREDENTIALS, {
                company_id: companyId,
                commerce_id: commerceId,
                branch_id: branchId,
                user,
              });
            }
          } catch (e) {
            // ignore refresh errors, but still resolve the original response
          }

          resolve(res);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  },

  [authType.actions.POST_MASSPAYMENT_CREDENTIALS]: async ({ dispatch, }, data) => {
    return new Promise((resolve, reject) => {
      httpCommpix.post('/security/masspayments/credentials', data)
        .then(async (res: AxiosResponse) => {
          // after creating/updating credentials, refresh the credentials list for the current scope
          try {
            const companyId = data.companyId || data.company_id;
            const commerceId = data.commerceId || data.commerce_id;
            const branchId = data.branchId || data.branch_id;
            const user = data.user;
            if (companyId && commerceId && branchId) {
              await dispatch(authType.actions.GET_MASSPAYMENT_CREDENTIALS, {
                company_id: companyId,
                commerce_id: commerceId,
                branch_id: branchId,
                user,
              });
            }
          } catch (e) {
            // ignore refresh errors, but still resolve the original response
          }

          resolve(res);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  },
  [authType.actions.GET_COMMPIX_CREDENTIALS]: async ({ commit, }, data) => {
    const { company_id: companyId, commerce_id: commerceId, branch_id: branchId, user, } = data;

    return new Promise((resolve, reject) => {
      httpCommpix
        .get(
          `/security/commpix/credentials/${companyId}/${commerceId}/${branchId}/${user}`
        )
        .then((res: AxiosResponse) => {
          // API now returns an object; commit the object directly
          commit(authType.mutations.SET_COMMPIX_CREDENTIALS, res.data);
          resolve(res);
        })
        .catch((error: AxiosError) => {
          if (error.response?.status === 404) {
            commit(authType.mutations.SET_COMMPIX_CREDENTIALS, {});
          }
          reject(error);
        });
    });
  },
  [authType.actions.GET_MASSPAYMENT_CREDENTIALS]: async ({ commit, }, data) => {
    const { company_id: companyId, commerce_id: commerceId, branch_id: branchId, user, } = data;

    return new Promise((resolve, reject) => {
      httpCommpix
        .get(
          `/security/masspayments/credentials/${companyId}/${commerceId}/${branchId}/${user}`
        )
        .then((res: AxiosResponse) => {
          // API now returns an object; commit the object directly
          commit(authType.mutations.SET_MASSPAYMENT_CREDENTIALS, res.data);
          resolve(res);
        })
        .catch((error: AxiosError) => {
          if (error.response?.status === 404) {
            commit(authType.mutations.SET_MASSPAYMENT_CREDENTIALS, {});
          }
          reject(error);
        });
    });
  },
  [authType.actions.GET_MANTECA_CREDENTIALS]: async ({ commit, }, data) => {
    const { company_id: companyId, commerce_id: commerceId, branch_id: branchId, } = data;

    return new Promise((resolve, reject) => {
      httpCommpix
        .get(
          `security/manteca/user/${companyId}/${commerceId}/${branchId}`
        )
        .then((res: AxiosResponse) => {
          // API returns an object with the UserAnyId property
          commit(authType.mutations.SET_MANTECA_CREDENTIALS, res.data);
          resolve(res);
        })
        .catch((error: AxiosError) => {
          if (error.response?.status === 404) {
            commit(authType.mutations.SET_MANTECA_CREDENTIALS, {});
          }
          reject(error);
        });
    });
  },
  [authType.actions.POST_PVS_CREDENTIALS]: async (_context, data) => {
    return new Promise((resolve, reject) => {
      httpCommpix
        .post('/pvs/credentials', data)
        .then((res: AxiosResponse) => {
          resolve(res);
        })
        .catch((error: AxiosError) => {
          reject(error);
        });
    });
  },
  [authType.actions.GET_PVS_CREDENTIALS]: async ({ commit, }, data) => {
    const { company_id: companyId, commerce_id: commerceId, branch_id: branchId, } = data;

    return new Promise((resolve, reject) => {
      httpCommpix
        .get(`/pvs/credentials/${companyId}/${commerceId}/${branchId}`)
        .then((res: AxiosResponse) => {
          commit(authType.mutations.SET_PVS_CREDENTIALS, res.data);
          resolve(res);
        })
        .catch((error: AxiosError) => {
          if (error.response?.status === 404) {
            commit(authType.mutations.SET_PVS_CREDENTIALS, {});
          }
          reject(error);
        });
    });
  },
  [authType.actions.AUTH_COMMPIX]: async ({ commit, dispatch, }, email) => {
    const clientIdentifier = getAuthPayload();
    const { branchId, companyId, commerceId, username, } = clientIdentifier;
    const config = { headers: {}, };
    const payload = { branchId, companyId, commerceId, email, };

    const tempAxios = axios.create({ baseURL: httpCommpix.defaults.baseURL, });
    axiosRetry(tempAxios, {
      retries: 4,
      retryDelay: (retryCount) => {
        const delay = Math.pow(2, retryCount - 1) * 1000;
        return delay;
      },
      retryCondition: (error: AxiosError) => {
        return !!(axiosRetry.isNetworkOrIdempotentRequestError(error) ||
          (error.response && error.response.status >= 500));
      },
    });

    return tempAxios.post('/login', payload, config)
      .then(async (res: AxiosResponse) => {
        commit(authType.mutations.SET_COMMPIX_AUTH_TOKEN, res.data);
        try {
          if (companyId && commerceId && branchId) {
            await dispatch(authType.actions.GET_COMMPIX_CREDENTIALS, {
              company_id: companyId,
              commerce_id: commerceId,
              branch_id: branchId,
              user: username,
            });
          }
        } catch (e) {
          // ignore refresh errors
        }
        return res;
      });
  },
};

const getToken = async (account = false): Promise<AxiosResponse> => {
  const UID: string | undefined = account
    ? process.env.VUE_APP_ACCOUNT_UID
    : process.env.VUE_APP_UID;
  const REDIRECT_URI: string | undefined = process.env.VUE_APP_REDIRECT_URI;
  const verifier = base64URLEncode(crypto.randomBytes(32));
  const codeChallenge = base64URLEncode(sha256(verifier));
  const authorize = {
    client_id: UID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  };

  try {
    const code: AxiosResponse = await http.post(
      'oauth/application/authorize',
      authorize
    );
    const getToken = {
      client_id: UID,
      code: code.data.redirect_uri.code,
      code_verifier: verifier,
      redirect_uri: REDIRECT_URI,
      grant_type: 'authorization_code',
    };
    const token: AxiosResponse = await http.post(
      'oauth/application/token',
      getToken
    );
    return token;
  } catch (err) {
    throw err;
  }
};

export default {
  namespaced,
  state,
  getters,
  mutations,
  actions,
};
